import type { ListBuilderConfiguration } from '../../types';
import { allLicensesList } from './lists/all-licenses';
import { atRiskList } from './lists/at-risk';
import { confirmedList } from './lists/confirmed';
import { expiredList } from './lists/expired';
import { expiringSoonList } from './lists/expiring-soon';
import { missingFilesList } from './lists/missing-files';
import { unmatchedList } from './lists/unmatched';

/**
 * "Current Licenses" list configuration (PD-13891).
 *
 * This is the tenant-local client_license surface — backed by GET /api/licenses
 * (LicensesService.findAll), which no longer issues cross-DB subqueries against
 * workd_mission_control on the hydrated query (the registrant name / file-count
 * DISPLAY values are still overlaid via a batched post-query fetch).
 *
 * Every tab here filters on LOCAL columns/tables only:
 * 1. Expiring Soon - renewal queue for licenses approaching expiration
 * 2. Expired - licenses that have lapsed
 * 3. At Risk - licenses with a recorded disciplinary action
 *    (tenant-local `license_disciplinary_action`)
 * 4. Missing Files - matched licenses with no supporting documentation
 * 5. Under Review - truly unmatched licenses with no candidate match
 * 6. Confirmed - audit trail of confirmed registry matches
 * 7. All Licenses - unfiltered view for ad-hoc searching
 *
 * REMOVED (PD-13891): "Pending Match" (relocated to the dedicated Pending
 * Matches surface, /licenses/matches), and the two registry-derived tabs
 * "Recently Changed" (lm_registry_changes) + "Discrepancies" (lm_registry) —
 * both relied on cross-DB subqueries that re-trigger the getMany hydration bug.
 * Re-sourcing those two without the cross-DB coupling is a deferred follow-up.
 */
export const LICENSE_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		expiringSoonList,
		expiredList,
		atRiskList,
		missingFilesList,
		unmatchedList,
		confirmedList,
		allLicensesList,
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	showResetButton: true,
	filters: {
		activeFilters: [
			'licenseKeywords',
			'licenseStatus',
			'licenseType',
			'licenseState',
			'licenseClient',
			'licenseClientLeadOrCustomer',
			'licenseSalesRep',
			'licenseExpirationDateRange',
			'licenseHasDisciplinary',
			'licenseHasFiles',
			'licenseMatchStatus',
			'licenseMatchConfidence',
			// PD-13891: licenseRecentlyChanged + licenseHasRegistryDiscrepancy
			// removed from the tray. Both resolve to cross-DB subqueries against
			// workd_mission_control that re-trigger the getMany hydration bug, so
			// they must not be selectable on this tenant-local surface.
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/licenses/:id',
			objectLabel: 'License',
			objectTable: 'client_license',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'licenseNumber', fieldLabel: 'License #' },
				{ fieldName: 'type', fieldLabel: 'Type' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'expirationDate', fieldLabel: 'Expiration Date' },
				{ fieldName: 'issueDate', fieldLabel: 'Issue Date' },
				{ fieldName: 'issuingState', fieldLabel: 'Issuing State' },
				{ fieldName: 'issuingAuthority', fieldLabel: 'Issuing Authority' },
				{ fieldName: 'hasDisciplinaryAction', fieldLabel: 'Has Disciplinary' },
				{ fieldName: 'fileCount', fieldLabel: 'Files' },
				{ fieldName: 'clientName', fieldLabel: 'Client' },
				{ fieldName: 'clientLeadOrCustomer', fieldLabel: 'Lead/Customer' },
				{ fieldName: 'clientStreetAddress', fieldLabel: 'Street Address' },
				{ fieldName: 'clientCityStateZip', fieldLabel: 'City/State/Zip' },
			],
		},
	],
};
